import { supabase } from "../db";

const adminSecurityTable = 'admin_security';

/**
 * Get security question and logs from admin_security table
 */
export const getSecurityData = async () => {
  try {
    const { data, error } = await supabase
      .from('admin_security')
      .select('*')
      .limit(1);  // Don't use .single() here

    if (error) {
      console.error('Error fetching security data:', error);
      throw error;
    }
    
    // Check if data exists
    if (!data || data.length === 0) {
      return { 
        success: false, 
        error: 'Security question not configured. Please contact administrator.' 
      };
    }
    
    return { success: true, data: data[0] };
  } catch (error) {
    console.error('Error fetching security data:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Add a new log entry to admin_security
 */
export const addSecurityLog = async (logEntry) => {
  try {
    // Get current security data
    const { data: security, error: fetchError } = await supabase
      .from(adminSecurityTable)
      .select('logs, id')
      .limit(1)
      .single();

    if (fetchError) throw fetchError;

    // Parse existing logs
    let currentLogs = [];
    try {
      currentLogs = JSON.parse(security.logs || '[]');
    } catch {
      currentLogs = [];
    }

    // Add new log
    const newLog = {
      id: currentLogs.length + 1,
      timestamp: new Date().toISOString(),
      ...logEntry
    };

    currentLogs.push(newLog);

    // Keep only last 100 logs
    if (currentLogs.length > 100) {
      currentLogs = currentLogs.slice(-100);
    }

    // Update in database
    const { data, error } = await supabase
      .from(adminSecurityTable)
      .update({ 
        logs: JSON.stringify(currentLogs),
        updated_at: new Date().toISOString()
      })
      .eq('id', security.id)
      .select()
      .single();

    if (error) throw error;
    return { success: true, data, logs: currentLogs };
  } catch (error) {
    console.error('Error adding log:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Verify security answer
 */
export const verifySecurityAnswer = async (providedAnswer) => {
  try {
    const { data, error } = await supabase
      .from(adminSecurityTable)
      .select('answer')
      .limit(1)
      .single();

    if (error) throw error;

    const isCorrect = data.answer.toLowerCase().trim() === providedAnswer.toLowerCase().trim();
    return { success: true, isCorrect };
  } catch (error) {
    console.error('Error verifying answer:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get all logs from admin_security
 */
export const getAllLogs = async () => {
  try {
    const { data, error } = await supabase
      .from(adminSecurityTable)
      .select('logs')
      .limit(1)

    if (error) throw error;

    let logs = [];
    try {
      logs = JSON.parse(data.logs || '[]');
    } catch {
      logs = [];
    }

    return { success: true, logs };
  } catch (error) {
    console.error('Error fetching logs:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Update security question and answer
 */
export const updateSecurityQuestion = async (question, answer) => {
  try {
    const { data: current, error: fetchError } = await supabase
      .from(adminSecurityTable)
      .select('id')
      .limit(1)

    if (fetchError) throw fetchError;

    const { data, error } = await supabase
      .from(adminSecurityTable)
      .update({ 
        question, 
        answer,
        updated_at: new Date().toISOString()
      })
      .eq('id', current.id)
      .select()
      .single();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Error updating security question:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Clear all logs
 */
export const clearAllLogs = async () => {
  try {
    const { data: current, error: fetchError } = await supabase
      .from(adminSecurityTable)
      .select('id')
      .limit(1)
      .single();

    if (fetchError) throw fetchError;

    const { data, error } = await supabase
      .from(adminSecurityTable)
      .update({ 
        logs: '[]',
        updated_at: new Date().toISOString()
      })
      .eq('id', current.id)
      .select()
      .single();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Error clearing logs:', error);
    return { success: false, error: error.message };
  }
};